/**
 * Sistema de Relatórios de Erro
 * Equivalente ao ErrorReporter do Flutter
 */
export class ErrorReporter {
  private static isInitialized = false;
  private static errorHandlers: ((error: Error, level: string) => void)[] = [];
  private static eventHandlers: ((message: string, level: string) => void)[] = [];

  static initialize(): void {
    if (this.isInitialized) return;

    // Captura erros JavaScript não tratados
    window.addEventListener('error', (event) => {
      this.captureException(
        new Error(event.message),
        'error',
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack
        }
      );
    });

    // Captura promises rejeitadas não tratadas
    window.addEventListener('unhandledrejection', (event) => {
      this.captureException(
        new Error(event.reason),
        'error',
        { type: 'unhandledrejection' }
      );
    });

    this.isInitialized = true;
  }

  static captureException(
    error: Error,
    level: 'debug' | 'info' | 'warning' | 'error' | 'fatal' = 'error',
    extra?: Record<string, any>
  ): void {
    const errorData = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      level,
      extra,
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Envia para handlers registrados
    this.errorHandlers.forEach(handler => {
      try {
        handler(error, level);
      } catch (e) {
        console.error('Error in error handler:', e);
      }
    });

    // Log para console em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      console.group(`🔴 Error Report [${level.toUpperCase()}]`);
      console.error('Error:', error);
      console.log('Extra data:', extra);
      console.groupEnd();
    }

    // Aqui você pode integrar com serviços como Sentry, LogRocket, etc.
    this.sendToExternalService(errorData);
  }

  static captureEvent(
    message: string,
    level: 'debug' | 'info' | 'warning' | 'error' = 'info',
    extra?: Record<string, any>
  ): void {
    const eventData = {
      message,
      timestamp: new Date().toISOString(),
      level,
      extra,
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    // Envia para handlers registrados
    this.eventHandlers.forEach(handler => {
      try {
        handler(message, level);
      } catch (e) {
        console.error('Error in event handler:', e);
      }
    });

    // Log para console em desenvolvimento
    if (process.env.NODE_ENV === 'development') {
      const emoji = level === 'error' ? '🔴' : level === 'warning' ? '🟡' : '🔵';
      console.log(`${emoji} Event [${level.toUpperCase()}]: ${message}`, extra);
    }

    this.sendToExternalService(eventData);
  }

  static addErrorHandler(handler: (error: Error, level: string) => void): void {
    this.errorHandlers.push(handler);
  }

  static addEventHandler(handler: (message: string, level: string) => void): void {
    this.eventHandlers.push(handler);
  }

  static removeErrorHandler(handler: (error: Error, level: string) => void): void {
    const index = this.errorHandlers.indexOf(handler);
    if (index > -1) {
      this.errorHandlers.splice(index, 1);
    }
  }

  static removeEventHandler(handler: (message: string, level: string) => void): void {
    const index = this.eventHandlers.indexOf(handler);
    if (index > -1) {
      this.eventHandlers.splice(index, 1);
    }
  }

  private static sendToExternalService(data: any): void {
    // Implementar integração com serviços externos
    // Exemplo: Sentry.captureException(data)
    // Exemplo: LogRocket.captureException(data)
    
    // Por enquanto, apenas simula o envio
    if (process.env.NODE_ENV === 'development') {
      console.log('📤 Sending to external service:', data);
    }
  }

  static dispose(): void {
    this.errorHandlers.length = 0;
    this.eventHandlers.length = 0;
    this.isInitialized = false;
  }
}
